// src/pages/doctor/DoctorLayout.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import axios from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { Menu, X } from 'lucide-react';

type SearchMap = Record<string, string>;
const SS_KEY = 'doctor.lastSearchByPath';

// sessionStorage ユーティリティ
const getSavedSearchMap = (): SearchMap => {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    return raw ? (JSON.parse(raw) as SearchMap) : {};
  } catch {
    return {};
  }
};
const saveSearchForPath = (path: string, search: string) => {
  const map = getSavedSearchMap();
  if (!search) {
    delete map[path];
  } else {
    map[path] = search;
  }
  sessionStorage.setItem(SS_KEY, JSON.stringify(map));
};
const getSearchForPath = (path: string): string => {
  const map = getSavedSearchMap();
  return map[path] || '';
};
const clearAllSavedSearch = () => {
  try { sessionStorage.removeItem(SS_KEY); } catch {}
};

const DoctorLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { auth, logout } = useAuth();

  // ログイン状態の簡易判定
  const storageToken = useMemo(() => localStorage.getItem('token') || '', [auth.token]);
  const effectiveToken = auth.token || storageToken;
  const loggedIn = !!effectiveToken;

  const [doctorName, setDoctorName] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false); // ▼ モバイルメニュー開閉

  useEffect(() => {
    const fetchDoctor = async () => {
      if (!loggedIn) {
        setDoctorName('');
        return;
      }
      try {
        const res = await axios.get('/doctor/me', {
          headers: { Authorization: `Bearer ${effectiveToken}` },
        });
        const name =
          res.data?.profile?.full_name ||
          res.data?.name ||
          res.data?.nickname ||
          res.data?.email ||
          '';
        setDoctorName(name);
      } catch {
        setDoctorName('');
      }
    };
    fetchDoctor();
  }, [loggedIn, effectiveToken]);

  // パスごとの検索クエリ保存
  useEffect(() => {
    if (location.pathname.startsWith('/doctor/')) {
      saveSearchForPath(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    const msg =
      t('doctor.confirm_logout') ||
      t('mypage.logout_confirm') ||
      'ログアウトしますか？';
    if (window.confirm(msg)) {
      clearAllSavedSearch();
      logout();
      navigate('/login/doctor');
    }
  };

  // ナビ定義（MyPage 先頭）
  const navItems = [
    { label: t('doctor.tab_mypage') || 'マイページ', path: '/doctor/mypage' },
    { label: t('doctor.tab_dashboard'), path: '/doctor/dashboard' },
    { label: t('doctor.tab_reservations'), path: '/doctor/reservations' },
    { label: t('doctor.tab_users'), path: '/doctor/users' },
    { label: t('doctor.tab_schedule'), path: '/doctor/schedule' },
  ];

  const goNav = (path: string) => {
    const saved = getSearchForPath(path);
    navigate({ pathname: path, search: saved || '' });
    setMobileOpen(false); // モバイル時は閉じる
  };

  useEffect(() => {
    const base = 'Melodis Clinic';
    const pageTitle =
      (location.pathname.startsWith('/doctor/mypage') && (t('doctor.tab_mypage') || 'マイページ')) ||
      (location.pathname.startsWith('/doctor/dashboard') && t('doctor.tab_dashboard')) ||
      (location.pathname.startsWith('/doctor/reservations') && t('doctor.tab_reservations')) ||
      (location.pathname.startsWith('/doctor/users') && t('doctor.tab_users')) ||
      (location.pathname.startsWith('/doctor/schedule') && t('doctor.tab_schedule')) ||
      t('doctor.title');
    document.title = `${pageTitle} | ${base}`;
  }, [location.pathname, i18n.language, t]);

  // ようこそテキスト
  const prefix =
    i18n.language === 'ja' ? 'ようこそ' : i18n.language === 'zh' ? '欢迎' : 'Welcome';
  const roleSuffix =
    i18n.language === 'ja' ? '医師' : i18n.language === 'zh' ? '医生' : 'Doctor';
  const fullWelcome = doctorName ? `${prefix} ${doctorName} ${roleSuffix}` : '';

  return (
    <div
      className="min-h-screen bg-background bg-gradient-hero text-base"
    >
      {/* ───────── HEADER / NAVBAR ───────── */}
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-md border-b border-border/40 shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          {/* 左：Logo + Title */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="shrink-0"
              onClick={() => goNav('/doctor/mypage')}
              aria-label="Home"
              title="Home"
            >
              <img src="/images/Melodislogo.png" alt="Logo" className="h-9 sm:h-10" />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold truncate">
              {t('doctor.title')}
            </h1>
          </div>

          {/* 中：デスクトップ ナビ（md以上表示） */}
          <nav className="hidden md:flex items-center gap-2 ml-4">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => goNav(item.path)}
                className={classNames(
                  'px-4 py-2 rounded-full text-sm md:text-base transition-all duration-200 whitespace-nowrap font-medium',
                  location.pathname.startsWith(item.path)
                    ? 'bg-coral text-white shadow-soft translate-y-[-1px]'
                    : 'text-gray-600 hover:text-coral hover:bg-coral/5'
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* 右：言語 / ようこそ / Logout（md以上は横並び） */}
          <div className="hidden md:flex items-center gap-2">
            {loggedIn && doctorName && (
              <span
                className="text-sm max-w-[360px] flex items-center gap-1"
                title={fullWelcome}
              >
                <span className="truncate">{`${prefix} ${doctorName}`}</span>
                <span className="whitespace-nowrap">{roleSuffix}</span>
              </span>
            )}

            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="text-sm border rounded-full px-3 py-1.5 bg-white"
              title={t('doctor.language') || 'Language'}
            >
              <option value="ja">日本語</option>
              <option value="zh">中文</option>
            </select>

            {loggedIn && (
              <button
                onClick={handleLogout}
                className="text-sm border border-red-200 bg-white text-red-600 px-4 py-2 rounded-full hover:bg-red-50 transition-colors"
              >
                {t('doctor.logout') || t('mypage.logout') || 'Logout'}
              </button>
            )}
          </div>

          {/* 右：モバイル トグル（md未満表示） */}
          <div className="md:hidden flex items-center gap-2">
            {/* 簡易ようこそ（省スペース） */}
            {loggedIn && doctorName && (
              <span className="text-sm max-w-[140px] truncate" title={fullWelcome}>
                {doctorName}
              </span>
            )}
            <button
              className="p-2 rounded-md hover:bg-gray-100"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* モバイルパネル（md未満） */}
        <div
          className={classNames(
            'md:hidden bg-white/95 backdrop-blur border-t shadow-sm transition-[max-height,opacity] duration-300 overflow-hidden',
            mobileOpen ? 'max-h-[480px] opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="px-4 py-3 flex flex-col gap-3">
            {/* ナビ一覧（縦） */}
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => goNav(item.path)}
                  className={classNames(
                    'px-3 py-2 rounded-lg text-sm transition text-left',
                    location.pathname.startsWith(item.path)
                      ? 'bg-coral text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* 言語 & ログアウト */}
            <div className="flex items-center justify-between gap-3 pt-2">
              <select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                className="text-sm border rounded-md px-3 py-1.5 bg-white"
                title={t('doctor.language') || 'Language'}
              >
                <option value="ja">日本語</option>
                <option value="zh">中文</option>
              </select>

              {loggedIn && (
                <button
                  onClick={handleLogout}
                  className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700"
                >
                  {t('doctor.logout') || t('mypage.logout') || 'Logout'}
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ───────── CONTENT ───────── */}
      <main className="px-4 sm:px-6 mt-6 w-full">
        <Outlet />
      </main>
    </div>
  );
};

export default DoctorLayout;
